/**
 * Phonetic Normalization for German Names and Terms
 * 
 * Converts strings to a phonetic representation that groups
 * similar-sounding characters and character groups together.
 * This enables fuzzy matching that respects German phonetics.
 * 
 * Examples:
 * - "Müller" and "Mueller" → "mular"
 * - "Schneider" and "Shneider" → "ʃnaidar"
 * - "Schäfer" and "Schaefer" → "ʃafar"
 * - "Meier", "Maier", "Meyer", "Mayer" → "maiar"
 */

/**
 * German phonetic normalization rules applied in order
 */
const PHONETIC_RULES: Array<{ pattern: RegExp; replacement: string }> = [
  // === Phase 1: Lowercase ===
  // (handled separately)
  
  // === Phase 2: Umlaut normalization ===
  // Handle written-out umlauts first (before single characters)
  { pattern: /ae/g, replacement: 'a' },
  { pattern: /oe/g, replacement: 'o' },
  { pattern: /ue/g, replacement: 'u' },
  // Then handle actual umlauts
  { pattern: /ä/g, replacement: 'a' },
  { pattern: /ö/g, replacement: 'o' },
  { pattern: /ü/g, replacement: 'u' },
  // ß normalization
  { pattern: /ß/g, replacement: 's' },
  { pattern: /ss/g, replacement: 's' },
  
  // === Phase 3: German consonant clusters ===
  // "sch", "sh", "sc" → internal marker "ʃ"
  { pattern: /sch/g, replacement: 'ʃ' },
  { pattern: /sh/g, replacement: 'ʃ' },
  { pattern: /sc(?=[eiy])/g, replacement: 'ʃ' }, // sc before e, i, y
  
  // "ch" sounds (after vowels = soft, otherwise = hard like k)
  { pattern: /ch/g, replacement: 'k' },
  { pattern: /ck/g, replacement: 'k' },
  { pattern: /c(?=[eiy])/g, replacement: 'z' }, // c before e, i, y → z sound
  { pattern: /c/g, replacement: 'k' }, // c otherwise → k
  
  // "ph" → "f"
  { pattern: /ph/g, replacement: 'f' },
  
  // "v" at word start often sounds like "f" in German
  { pattern: /\bv/g, replacement: 'f' },
  // "v" within words often sounds like "w" or "f"
  { pattern: /v/g, replacement: 'f' },
  
  // "th" → "t" (no th-sound in German)
  { pattern: /th/g, replacement: 't' },
  
  // "dt" → "t"
  { pattern: /dt/g, replacement: 't' },
  
  // "tz" → "z"
  { pattern: /tz/g, replacement: 'z' },
  
  // "qu" → "kw"
  { pattern: /qu/g, replacement: 'kw' },
  
  // "x" → "ks"
  { pattern: /x/g, replacement: 'ks' },
  
  // === Phase 4: Vowel combinations ===
  // "ei", "ey", "ai", "ay" → "ai" (all sound similar)
  { pattern: /ei/g, replacement: 'ai' },
  { pattern: /ey/g, replacement: 'ai' },
  { pattern: /ay/g, replacement: 'ai' },
  // Keep "ai" as is (it's our target)
  
  // "ie" → long "i"
  { pattern: /ie/g, replacement: 'i' },
  
  // "eu", "äu" → "oi" sound
  { pattern: /eu/g, replacement: 'oi' },
  { pattern: /äu/g, replacement: 'oi' },
  { pattern: /au/g, replacement: 'au' }, // Keep "au" distinct
  
  // "y" as vowel → "i"
  { pattern: /y/g, replacement: 'i' },
  
  // === Phase 5: Silent letters ===
  // "h" after vowels (lengthening h) - remove
  { pattern: /([aeiou])h(?=[^aeiou]|$)/g, replacement: '$1' },
  
  // === Phase 6: Double consonant reduction ===
  { pattern: /mm/g, replacement: 'm' },
  { pattern: /nn/g, replacement: 'n' },
  { pattern: /ll/g, replacement: 'l' },
  { pattern: /tt/g, replacement: 't' },
  { pattern: /ff/g, replacement: 'f' },
  { pattern: /rr/g, replacement: 'r' },
  { pattern: /pp/g, replacement: 'p' },
  { pattern: /bb/g, replacement: 'b' },
  { pattern: /dd/g, replacement: 'd' },
  { pattern: /gg/g, replacement: 'g' },
  { pattern: /kk/g, replacement: 'k' },
  { pattern: /zz/g, replacement: 'z' },
  
  // === Phase 7: Final cleanup ===
  // Reduce any remaining double letters
  { pattern: /(.)\1+/g, replacement: '$1' },
];

/**
 * Normalize a string to its phonetic representation
 * @param input - The string to normalize
 * @returns Phonetically normalized string
 */
export function normalizePhonetic(input: string): string {
  if (!input) return '';
  
  // Start with lowercase
  let result = input.toLowerCase().trim();
  
  // Apply all phonetic rules in order
  for (const rule of PHONETIC_RULES) {
    result = result.replace(rule.pattern, rule.replacement);
  }
  
  // Remove any non-letter characters for pure phonetic comparison
  result = result.replace(/[^a-zʃ]/g, '');
  
  return result;
}

/**
 * Normalize multiple words and return normalized versions
 * Useful for multi-term search
 */
export function normalizePhoneticTerms(terms: string[]): string[] {
  return terms.map(term => normalizePhonetic(term)).filter(t => t.length > 0);
}

/**
 * Check if two strings are phonetically similar
 * Returns true if they normalize to the same phonetic representation
 */
export function arePhoneticallySimilar(a: string, b: string): boolean {
  return normalizePhonetic(a) === normalizePhonetic(b);
}

/**
 * Common German name variants that should match each other
 * This is used for testing and documentation
 */
export const GERMAN_NAME_VARIANTS = {
  meier: ['Meier', 'Maier', 'Meyer', 'Mayer', 'Meir', 'Mair'],
  mueller: ['Müller', 'Mueller', 'Muller', 'Mueler'],
  schaefer: ['Schäfer', 'Schaefer', 'Schefer', 'Shafer'],
  schneider: ['Schneider', 'Shneider', 'Scneider'],
  fischer: ['Fischer', 'Fisher', 'Ficher'],
  wagner: ['Wagner', 'Vagner', 'Wegner'],
  bauer: ['Bauer', 'Baur', 'Baier', 'Beier'],
  hoffmann: ['Hoffmann', 'Hofmann', 'Hoffman'],
  schmidt: ['Schmidt', 'Schmitt', 'Schmid', 'Schmied'],
  koch: ['Koch', 'Kock', 'Kokh'],
};
